"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useAtomValue, useStore } from "jotai";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import type { Edge } from "@xyflow/react";

import {
  graphNodesAtom,
  edgesAtom,
  nodeRegistryAtom,
  nodeDataMapAtom,
  dynamicNodeConfigsAtom,
  activeFlowIdAtom,
} from "@/store/editor-v2";
import type { NodeDefinition } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";

import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/shadcn/command";
import { DataTypeBadge } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";

import { BindableInput } from "./BindableInput";
import { VariableSourceIcon } from "./VariablePickerPanel";
import { useVariableOptions } from "./VariablePicker";
import { buildOptionsByValue } from "./variableOptions";
import { useSecretsSources } from "./useSecretsSources";
import type { SecretLookupEntry } from "./mentionSource";
import { VariableRefChip } from "./VariableRefChip";
import { createBoundSourceHighlight } from "./useBoundSourceHighlight";
import {
  resolveVariableRefStatus,
  type VariableRefStatus,
} from "./variableRefStatus";
import { formatVarRefToken, WHOLE_VAR_REF_PATTERN } from "./variableRefs";

/**
 * V2 renderer for `display=ConfigBindableChipsSelect`. A chips selector whose
 * entries are either literal options (from `values`) or `:var[<ref>]` references
 * to upstream `str` outputs. One searchable popover offers both.
 *
 * `isMulti` switches between:
 *   - MULTI: a `list` field. Entries are listed as removable rows below the add
 *     control; the whole field can also be bound to one `list[str]` output via
 *     {@link BindableInput}'s `{}` affordance.
 *   - SINGLE: a `str` field. A standard single-select — one literal *or* one
 *     `:var[<ref>]` to a `str` output, shown inline and replaced on re-pick.
 *
 * Both shapes resolve via the backend deep-walk: per-item refs resolve each
 * element; a whole-string ref keeps its resolved type.
 */

type Option = { value: string; label: string };

function normalizeValues(values: unknown): Option[] {
  if (!Array.isArray(values)) return [];
  return values
    .map((v): Option | null => {
      if (typeof v === "string") return { value: v, label: v };
      if (v && typeof v === "object") {
        const value = (v as { value?: unknown }).value;
        const label = (v as { label?: unknown }).label;
        if (typeof value === "string") {
          return { value, label: typeof label === "string" ? label : value };
        }
      }
      return null;
    })
    .filter((o): o is Option => o !== null);
}

const parseWholeRef = (s: unknown): string | null => {
  if (typeof s !== "string") return null;
  const m = s.match(WHOLE_VAR_REF_PATTERN);
  return m ? m[1] : null;
};

export function BindableChipsSelectField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  values,
  isMulti = false,
  disabled,
  isRequired,
  showOptionalLabel,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  values?: unknown;
  isMulti?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const single = typeof raw === "string" ? raw : "";
  const items: string[] = useMemo(
    () =>
      Array.isArray(raw)
        ? raw.filter((v): v is string => typeof v === "string")
        : [],
    [raw]
  );
  const options = useMemo(() => normalizeValues(values), [values]);
  const invalid = !!error;

  // ── Variable sources (entries bind to scalar `str` outputs) ────────────────
  const nodes = useAtomValue(graphNodesAtom);
  const edges = useAtomValue(edgesAtom) as Edge[];
  const nodeRegistry = useAtomValue(nodeRegistryAtom) as NodeDefinition[];
  const nodeDataMap = useAtomValue(nodeDataMapAtom);
  const dynamicNodeConfigs = useAtomValue(dynamicNodeConfigsAtom);
  const { spotGroup } = useSpotUser();
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const { secrets } = useSecretsSources(spotGroup?.id, activeFlowId);
  const secretsByQualifiedId = useMemo(() => {
    const m = new Map<string, SecretLookupEntry>();
    for (const s of secrets) m.set(`${s.scope}:${s.id}`, s);
    return m;
  }, [secrets]);

  const allOptions = useVariableOptions(
    nodes,
    nodeId,
    edges,
    nodeRegistry,
    "str",
    fieldKey
  );
  const optionsByValue = useMemo(
    () => buildOptionsByValue(allOptions),
    [allOptions]
  );
  const compatibleNodes = useMemo(
    () => allOptions.filter((n) => n.hasCompatible),
    [allOptions]
  );

  const [open, setOpen] = useState(false);

  // Per-chip hover → origin-node highlight; chips render in a map, so the
  // handlers are plain closures over the store rather than hook calls.
  const store = useStore();
  const chipHighlight = (refId: string | null) =>
    createBoundSourceHighlight(store, refId);

  const setField = (next: string | string[]) =>
    form.setValue(path, next, { shouldDirty: true, shouldTouch: true });

  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  const refStatus = (refId: string): VariableRefStatus => {
    const selected = optionsByValue.get(refId);
    if (selected) {
      return {
        kind: "valid",
        meta: {
          sourceLabel: selected.node.nodeName,
          sourceImage: selected.node.nodeImage,
          sourceIconName: selected.node.nodeIconName,
          sourceColor: selected.node.nodeColor,
          variableLabel: selected.output.outputLabel,
          dataType: selected.output.dataType,
          isList: selected.output.isList,
        },
      };
    }
    return resolveVariableRefStatus(refId, {
      nodeId,
      nodeDataMap,
      edges,
      nodeRegistry,
      dynamicNodeConfigs,
      secretsByQualifiedId,
    });
  };

  // Shared picker popover: `Options` (literals) + one group per upstream node.
  // Selected entries stay listed and are marked with a check; clicking one
  // again is a toggle (handled by the onLiteral/onRef callbacks).
  const picker = (
    trigger: ReactNode,
    available: Option[],
    onLiteral: (value: string) => void,
    onRef: (ref: string) => void,
    selected: { literals: ReadonlySet<string>; refs: ReadonlySet<string> }
  ) => (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0 z-50"
        align="start"
        sideOffset={6}
      >
        <Command>
          <CommandInput placeholder="Search values and variables…" />
          <CommandList className="max-h-[360px] py-1">
            <CommandEmpty>No matches.</CommandEmpty>
            {available.length > 0 && (
              <CommandGroup
                heading={
                  <span className="text-sm font-medium text-muted-foreground">
                    Options
                  </span>
                }
              >
                {available.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={`option ${o.label} ${o.value}`}
                    onSelect={() => onLiteral(o.value)}
                    className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    <Check
                      size={14}
                      className={cn(
                        "shrink-0",
                        selected.literals.has(o.value)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {available.length > 0 && compatibleNodes.length > 0 && (
              <CommandSeparator className="my-1" />
            )}
            {compatibleNodes.map((node) => (
              <CommandGroup
                key={node.nodeId}
                value={node.nodeId}
                heading={
                  <span className="flex items-center gap-2">
                    <VariableSourceIcon
                      image={node.nodeImage}
                      iconName={node.nodeIconName}
                      color={node.nodeColor}
                      isSecret={node.nodeId === "__secrets__"}
                    />
                    <span className="truncate text-sm font-medium text-muted-foreground">
                      {node.nodeName}
                    </span>
                  </span>
                }
              >
                {node.outputs
                  .filter((o) => o.compatible)
                  .map((o) => (
                    <CommandItem
                      key={o.value}
                      value={`${node.nodeName} ${o.outputLabel} ${o.value}`}
                      onSelect={() => onRef(o.value)}
                      className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        {o.outputLabel}
                      </span>
                      <DataTypeBadge
                        dataType={o.dataType}
                        isList={o.isList}
                        className="shrink-0"
                      />
                      <Check
                        size={14}
                        className={cn(
                          "shrink-0",
                          selected.refs.has(o.value)
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const labeled = (body: ReactNode) => (
    <div className="flex flex-col gap-1.5">
      <NoxusFormLabel
        label={label}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      {body}
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );

  // ── SINGLE select — one literal or one `:var[ref]`, shown inline ───────────
  if (!isMulti) {
    const ref = parseWholeRef(single);
    const singleSelected = {
      literals: new Set(single && !ref ? [single] : []),
      refs: new Set(ref ? [ref] : []),
    };
    const onLiteral = (value: string) => {
      setField(value);
      setOpen(false);
    };
    const onRef = (r: string) => {
      setField(formatVarRefToken(r));
      setOpen(false);
    };

    const trigger = (
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-active={open}
            className={cn(
              "flex h-8 w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-transparent pl-2.5 text-left text-sm shadow-sm transition-colors outline-none",
              single ? "pr-8" : "pr-9 text-muted-foreground",
              "hover:bg-accent/40 data-[active=true]:ring-ring data-[active=true]:ring-1",
              "focus-visible:ring-ring focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            {single ? (
              ref ? (
                <VariableRefChip
                  size="sm"
                  status={refStatus(ref)}
                  onMouseEnter={chipHighlight(ref).highlight}
                  onMouseLeave={chipHighlight(ref).unhighlight}
                />
              ) : (
                <span className="truncate text-foreground">
                  {labelByValue.get(single) ?? single}
                </span>
              )
            ) : (
              <span className="truncate">Select column…</span>
            )}
          </button>
        </PopoverTrigger>
        {single ? (
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              setField("");
            }}
            className={cn(
              "absolute right-1 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded",
              "text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
            )}
            aria-label="Clear"
          >
            <X size={13} />
          </button>
        ) : (
          <ChevronDown
            size={14}
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60"
          />
        )}
      </div>
    );

    return labeled(
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-warning-400/70"
        )}
      >
        {picker(trigger, options, onLiteral, onRef, singleSelected)}
      </div>
    );
  }

  // ── MULTI select — removable rows below, plus whole-field list binding ──────
  // Clicking an entry toggles it and keeps the popover open, so several
  // values can be picked in one pass.
  const multiSelected = {
    literals: new Set(items),
    refs: new Set(
      items.map(parseWholeRef).filter((r): r is string => r !== null)
    ),
  };
  const toggleLiteral = (value: string) =>
    setField(
      items.includes(value)
        ? items.filter((i) => i !== value)
        : [...items, value]
    );
  const toggleRef = (r: string) => {
    const token = formatVarRefToken(r);
    setField(
      items.includes(token)
        ? items.filter((i) => i !== token)
        : [...items, token]
    );
  };

  const addTrigger = (
    <button
      type="button"
      disabled={disabled}
      data-active={open}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-md border border-input bg-transparent px-2.5 text-left text-sm text-muted-foreground shadow-sm transition-colors outline-none",
        "hover:bg-accent/40 data-[active=true]:ring-ring data-[active=true]:ring-1",
        "focus-visible:ring-ring focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50"
      )}
    >
      <Plus size={14} className="shrink-0 text-muted-foreground/70" />
      <span className="truncate">Add value or variable…</span>
    </button>
  );

  return labeled(
    <>
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-warning-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          value={typeof raw === "string" ? raw : ""}
          dataType="str"
          inputName={fieldKey}
          disabled={disabled}
          renderLiteral={picker(
            addTrigger,
            options,
            toggleLiteral,
            toggleRef,
            multiSelected
          )}
          onChange={(next) =>
            form.setValue(path, next, { shouldDirty: true, shouldTouch: true })
          }
        />
      </div>

      {/* Selected entries wrap inline below the input as removable chips. */}
      {items.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {items.map((item, idx) => {
            const ref = parseWholeRef(item);
            return (
              <li
                key={`${item}-${idx}`}
                className="inline-flex h-6 max-w-full items-center gap-0.5 rounded border border-border bg-background pr-0.5 text-sm shadow-sm"
              >
                {ref ? (
                  <VariableRefChip
                    bare
                    size="sm"
                    status={refStatus(ref)}
                    onMouseEnter={chipHighlight(ref).highlight}
                    onMouseLeave={chipHighlight(ref).unhighlight}
                  />
                ) : (
                  <span className="truncate pl-2">
                    {labelByValue.get(item) ?? item}
                  </span>
                )}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    // Removing the hovered chip skips its mouseleave — release
                    // the canvas highlight explicitly.
                    chipHighlight(ref).unhighlight();
                    setField(items.filter((_, i) => i !== idx));
                  }}
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors",
                    "hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                    "disabled:opacity-50"
                  )}
                  aria-label="Remove"
                >
                  <X size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
